import React, { useEffect, useState } from "react";
import { Spin, Table } from "antd";

import AdminHeader from "../../components/AdminHeader";
import PlanUpgradePrompt from "../../components/PlanUpgradePrompt";
import ClickStats from "../../components/ClickStats";
import { useLinkProfile } from "../../api/useLinkProfile";
import { usePlanAccess } from "../../api/PlanAccess";
import { useStoreSelection } from "../../api/StoreSelection";
import { LinkProfile } from "../../models";

interface Stats {
  total_link_clicks: number;
  total_links: number;
  last_7_days: number;
  mtd: number;
  last_month: number;
  ytd: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    total_link_clicks: 0,
    total_links: 0,
    last_7_days: 0,
    mtd: 0,
    last_month: 0,
    ytd: 0,
  });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const [statsDetail, setStatsDetail] = useState<
    {
      date: string;
      clicks: number;
    }[]
  >([]);

  const {
    loading: linkProfileLoading,
    linkProfiles,
    getLinkProfiles,
    linkProfileStats,
    linkProfileStatsDetail,
  } = useLinkProfile();

  const { loading: planLoading, hasAnalytics, canAccessStores } =
    usePlanAccess();
  const { selectedStoreId, loading: storeSelectionLoading } =
    useStoreSelection();

  const activeStoreId =
    canAccessStores && selectedStoreId != null ? selectedStoreId : null;
  const waitingForStore =
    canAccessStores && (storeSelectionLoading || selectedStoreId == null);

  useEffect(() => {
    if (waitingForStore) {
      return;
    }

    setLoading(true);
    getLinkProfiles(activeStoreId)
      .then((profiles) => {
        setStats((prev) => ({
          ...prev,
          total_links: profiles.length,
        }));
      })
      .catch((err) => {
        console.error("Failed to load link profiles", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [waitingForStore, activeStoreId, getLinkProfiles]);

  useEffect(() => {
    if (!hasAnalytics || waitingForStore) {
      return;
    }

    linkProfileStats(activeStoreId)
      .then((data) => {
        setStats((prevStats) => ({
          ...prevStats,
          total_link_clicks: data.total,
          total_links: data.total_links ?? prevStats.total_links,
          last_7_days: data.last_7_days,
          mtd: data.mtd,
          last_month: data.last_month,
          ytd: data.ytd,
        }));
      })
      .catch((err) => {
        console.error("Failed to load link profile stats", err);
      });

    setDetailLoading(true);
    linkProfileStatsDetail("day", activeStoreId)
      .then((data: Record<string, number>) => {
        setStatsDetail(
          Object.entries(data).map(([date, clicks]) => ({
            date,
            clicks,
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to load link profile stats detail", err);
      })
      .finally(() => {
        setDetailLoading(false);
      });
  }, [
    hasAnalytics,
    waitingForStore,
    activeStoreId,
    linkProfileStats,
    linkProfileStatsDetail,
  ]);

  const columns = [
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string) => (path === "" ? "(Main link)" : path),
    },
    {
      title: "Clicks",
      dataIndex: "link_counter",
      key: "link_counter",
      sorter: (a: LinkProfile, b: LinkProfile) =>
        (a.link_counter || 0) - (b.link_counter || 0),
    },
  ];

  const detailsColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      key: "clicks",
    },
  ];

  const showAnalytics = !planLoading && hasAnalytics;

  return (
    <AdminHeader>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-start gap-4 mb-2">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          </div>

          {loading || waitingForStore ? (
            <div className="flex justify-center mt-4">
              <Spin size="large" />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Clicks
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {showAnalytics ? stats.total_link_clicks : "—"}
                  </dd>
                </div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Links
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    {stats.total_links}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {planLoading || waitingForStore ? (
            <div className="flex justify-center mt-8">
              <Spin />
            </div>
          ) : !hasAnalytics ? (
            <div className="mt-8">
              <PlanUpgradePrompt
                title="Click analytics require Starter"
                description="Hourly click charts and detailed reporting are available on Starter and Pro plans."
              />
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-3 sm:p-4 text-center">
                    <dt className="text-xs font-medium text-gray-500 truncate">
                      Last 7 Days
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-gray-900">
                      {stats.last_7_days}
                    </dd>
                  </div>
                </div>
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-3 sm:p-4 text-center">
                    <dt className="text-xs font-medium text-gray-500 truncate">
                      MTD
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-gray-900">
                      {stats.mtd}
                    </dd>
                  </div>
                </div>
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-3 sm:p-4 text-center">
                    <dt className="text-xs font-medium text-gray-500 truncate">
                      Last Month
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-gray-900">
                      {stats.last_month}
                    </dd>
                  </div>
                </div>
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-3 sm:p-4 text-center">
                    <dt className="text-xs font-medium text-gray-500 truncate">
                      YTD
                    </dt>
                    <dd className="mt-1 text-xl font-semibold text-gray-900">
                      {stats.ytd}
                    </dd>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <ClickStats storeId={activeStoreId} />
              </div>

              <div className="mt-8 w-full">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      Link Performance
                    </h2>
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                      <Table
                        dataSource={linkProfiles}
                        columns={columns}
                        rowKey="id"
                        showSorterTooltip={false}
                        pagination={{ pageSize: 10 }}
                        loading={linkProfileLoading}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      Performance By Date
                    </h2>
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                      <Table
                        dataSource={statsDetail}
                        columns={detailsColumns}
                        rowKey="date"
                        showSorterTooltip={false}
                        loading={detailLoading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminHeader>
  );
};

export default Dashboard;
